import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import shortid from 'shortid';
import { toNumber } from '../../utils/numbers';
import { AdmixDistribution } from '../../constants/MaterialModels';
import convertUnit from '../../utils/unit-converter';
import {
  getPrimaryDischarge,
  calculateWashWater,
  calculateWaterTotals,
  applyDropAdjustments,
} from '../../containers/Batch/utils/water-utils';
import { isFeatureEnabled } from '../../utils/feature-flags';
import {
  MAXIMUM_JOG_OPEN_TIME,
  MINIMUM_JOG_OPEN_TIME,
} from './jog/dynamic-jog';
import BatchData from '../../containers/Batch/BatchData';
import type { MixDesignItem } from '../../../types/batch/MixDesignItem';
import type {
  IBin,
  IDevice,
  IScale,
} from '../../../types/device/DeviceModels';
import { removeReplayMouse } from '../../utils/mouse-tracker/mouse-tracker';
import type {
  IMeteredDataToBatch,
  IWeighedDataToBatch,
} from '../../../types/batch/DataToBatch';
import type { IMaterial } from '../../../types/material/MaterialModels';
import { deepCopy } from '../../utils/data-manipulation';

const FEATURE_AUTO_TUNING = isFeatureEnabled('auto_tuning');

export const batchLifecycleMixin = {

  onStartReplayLoad(this: IBatchStore, currentIndex: number) {
    if (this.loadHistory.length && currentIndex === 0) {
      const { devices, deviceGUIs } = this.devicesStore;
      const { materials, materialDetails } = this.materialStore;
      const data = this.loadHistory.find(entry => entry.running?.length);

      if (!data || !!this.devicesStore._devices?.length) {
        return;
      }

      const batchData = deepCopy(data.running);

      this.materialStore._materials = deepCopy(materials);
      this.materialStore._materialDetails = deepCopy(materialDetails);
      this.devicesStore._deviceGUIs = deepCopy(deviceGUIs);
      this.devicesStore._devices = deepCopy(devices);

      if (data.gui) {
        this.devicesStore.deviceGUIs = data.gui;
      }

      batchData.forEach((data: IBatchData) => {
        if (data.scales) {
          Object.keys(data.scales).forEach(scaleId => {
            data.scales[scaleId].forEach((scaleData: IWeighedDataToBatch) => {

              const materialIndex = materials.findIndex((m: IMaterial) => m.itemId === scaleData.material?.itemId);
              if (materialIndex !== -1) {
                materials[materialIndex] = scaleData.material;
                materialDetails[scaleData.material.materialDetailId] = scaleData.materialDetails;
              }

              const bin = scaleData.bin;
              const binIndex = devices.findIndex((device: IDevice) => device.id === bin?.id);
              if (bin && binIndex !== -1) {
                devices[binIndex] = bin;
              } else if (bin && binIndex === -1) {
                devices.push(bin);
              }

              const scale = scaleData.scale;
              const scaleIndex = devices.findIndex((device: IDevice) => device.id === scale?.id);
              if (scale && scaleIndex !== -1) {
                devices[scaleIndex] = scale;
              } else if (scale && scaleIndex === -1) {
                devices.push(scaleData.scale);
              }

            });
          });
        }
        if (data.pumps) {
          Object.keys(data.pumps).forEach(pumpId => {
            const pumpData: IMeteredDataToBatch = data.pumps[pumpId];

            const materialIndex = materials.findIndex((m: IMaterial) => m.itemId === pumpData.material?.itemId);
            if (materialIndex !== -1) {
              materials[materialIndex] = pumpData.material;
              materialDetails[pumpData.material.materialDetailId] = pumpData.materialDetails;
            }

            const pump = pumpData.pump;
            const pumpIndex = devices.findIndex((device: IDevice) => device.id === Number(pumpId));
            if (pump && pumpIndex !== -1) {
              devices[pumpIndex] = pump;
            } else if (pump && pumpIndex === -1) {
              devices.push(pumpData.pump);
            }
          });
        }
      });
      this.materialStore.setMaterialsWithDetails();

    }
  },

  finishReplayLoad(this: IBatchStore) {
    if (this.loadHistory.length && this.replayMode) {
      this.loadHistory = [];
      this.clearAllTimers();
      this.setInitialData();
      this.queue = [];
      this.orderId = null;

      this.pacSimulator.init();
      this.pacSimulator.reset();
      this.replayMode = false;
      removeReplayMouse();
      this.start();

      this.timers.set(timerKey.finishReplayAndReload(), () => {
        this.devicesStore.deviceGUIs = [ ...this.devicesStore._deviceGUIs ];
        this.devicesStore.devices = [ ...this.devicesStore._devices ];
        this.materialStore.materials = [ ...this.materialStore._materials ];
        this.materialStore.materialDetails = { ...this.materialStore._materialDetails };
        this.devicesStore._deviceGUIs = [];
        this.devicesStore._devices = [];
        this.materialStore._materials = [];
        this.materialStore._materialDetails = {};
        this.materialStore.setMaterialsWithDetails();
      }, 0, 'Finish replay and reload the previous state');

      this.timers.set(timerKey.finishReplay(), () => {
        this.canEmit = true;
      }, 3000, 'Finish replay load');
    }
  },

  saveLog(this: IBatchStore, log: string) {
    if (!this.logs.includes(log)) {
      this.logs.push(log);
    }
  },

  displayLogs(this: IBatchStore, logs: string[] = []) {
    logs.forEach(v => {
      logger.log('replay:', v);
    });
  },

  displayLogsNotification(this: IBatchStore, logs: string[] = []) {
    logs.forEach(log => {
      if (log.includes('n-error')) {
        this.showError(`Remote - ${log.replace('n-error', '')}`);
      } else if (log.includes('n-warning')) {
        this.showWarning(`Remote - ${log.replace('n-warning', '')}`);
      } else if (log.includes('n-info')) {
        this.showInfo(`Remote - ${log.replace('n-info', '')}`);
      }
    });
  },

  /**
   * Auto batch start
   *
   * @param data
   */
  autoBatch(this: IBatchStore, data: IBatchData) {
    logger.info('autoBatch', data);

    if (!this.running.length) {
      this.clearDeviceStatus();
      this.discharging = false;

      if (this.isDemo) {
        this.pac.reset();
      }
    }

    const drops = this.splitDataForDrops(data);
    this.addToQueue(drops);

    this.checkPacConnected()
      .then(() => {

        if (!this.isRunning) {
          this.start();
        }

        this.startBatch();
      })
      .catch(() => {
        logger.warn('Plant disconnected!');
        this.showError('Plant disconnected!');
      });

  },

  /**
   * Add drops to queue
   *
   * @param drops
   */
  addToQueue(this: IBatchStore, drops: IBatchData[]) {
    drops.forEach(drop => {
      if (drop.mixDesign) {
        this.queue.push(drop);
      } else {
        this.queue.unshift(drop);
      }
    });
  },

  adjustAdmixturesForDrop(this: IBatchStore, drop: IBatchData): IBatchData | any {
    if (!drop.mixDesign) {
      return new BatchData(drop);
    }

    let mixDesign: any[] = [];
    drop.mixDesign.forEach(constituent => {
      const material = constituent.batchCode ? this.materialStore.getMaterialByBatchCode(constituent.batchCode) : this.materialStore.getMaterialByItemId(constituent.constituentItemId);
      const md = material ? this.materialStore.materialDetails[material.materialDetailId] : null;
      let c = { ...constituent };
      c.quantityFinalOriginal = c.quantityFinal;

      if (md && this.materialStore.isAdmixture(material.itemId)) {
        const quantity = toNumber(c.quantityFinal * drop.allDrops, 2);

        if (md.dropProportion === AdmixDistribution.BatchAllAdmixInFirstDrop) {
          if (drop.currentDrop === 1) {
            c.quantityFinal = quantity;
            c.quantityNet = quantity;
          } else {
            c.quantityFinal = 0;
            c.quantityNet = 0;
          }
        } else if (md.dropProportion === AdmixDistribution.BatchAllAdmixInLastDrop) {
          if (drop.currentDrop === drop.allDrops) {
            c.quantityFinal = quantity;
            c.quantityNet = quantity;
          } else {
            c.quantityFinal = 0;
            c.quantityNet = 0;
          }
        }

        logger.log('admix distribution, drop:', drop.currentDrop, material.code, c.quantityFinal, md.dropProportion);
      }

      mixDesign.push(c);
    });

    return { ...drop, mixDesign };
  },

  /**
   * Split data for drops
   */
  splitDataForDrops(this: IBatchStore, data: IBatchData): IBatchData[] | any[] {
    let drops = [];
    data.uid = shortid.generate();
    data.mixDesign = Array.isArray(data.mixDesign) ? this.materialStore.setConstituentTypeAndOrderForMixDesign(data.mixDesign) : null;

    for (let i = 1; i <= data.allDrops; i++) {
      const sequence = this.getSequence(data);
      let drop = { ...data, currentDrop: i, sequence };
      drop = { ...drop,  ...this.getTotalHeadWashWater(drop) };
      drop = this.adjustAdmixturesForDrop(drop);
      drops.push(new BatchData(drop));
    }

    return drops;
  },

  getAllWatersFromMixDesign(this: IBatchStore, mixDesign: MixDesignItem[]) {
    return mixDesign.filter(v => this.materialStore.isWater(v.constituentItemId) || this.materialStore.isSecondWater(v.constituentItemId));
  },

  getHeadWashWaterTarget(
    this: IBatchStore,
    data: IBatchData,
    mft: number,
    materialCode: string,
    waterScale?: IScale,
    waterUnit?: string,
  ) {
    let output = {
      headWater: 0,
      washWater: 0,
    };

    if (!data?.mixDesign) {
      return output;
    }

    let waterScaleCapacity = undefined;
    if (waterScale && waterUnit) {
      const waterScaleUnit = this.materialStore.getUnitSymbolByUnitId(waterScale.unitId);
      waterScaleCapacity = convertUnit(waterScale.capacity, waterScaleUnit, waterUnit);
    }

    const sequence = this.getSequence(data);
    const waters = this.getAllWatersFromMixDesign(data.mixDesign);

    // Get total head water for all waters (needed for overflow calculation)
    // @ts-ignore
    const totals: any = calculateWaterTotals({
      waters,
      dropSize: data.dropSize,
      allDrops: data.allDrops,
      sequence,
    });

    // Calculate head/wash for this specific material's mft
    const primary = getPrimaryDischarge(sequence);
    const washWater = calculateWashWater(primary, mft, waters.length);
    const headWater = mft - washWater;

    output = applyDropAdjustments({
      headWater,
      washWater,
      sequence,
      currentDrop: data.currentDrop,
      allDrops: data.allDrops,
      waterScaleCapacity,
      totalHeadWaterForAllWaters: totals.totalHeadWater,
    });

    logger.log(sequence.name, 'all head water on first drop:', sequence.allWaterOnFirstDrop, 'all wash water on last drop', sequence.allWashWaterOnLastDrop, data.currentDrop, data.allDrops);
    logger.log(`water target for ${materialCode}`, 'mft:', mft, 'headWater: ', output.headWater, 'washWater', output.washWater, 'total wash:', primary?.washAmount, primary?.washAmountType, 'water scale cap:', waterScaleCapacity);

    return output;
  },

  /**
   * Calculate washWater and headWater for drop
   */
  getTotalHeadWashWater(this: IBatchStore, data: IBatchData) {
    let d = {
      headWater: 0,
      washWater: 0,
    };

    if (!data?.mixDesign) {
      return d;
    }

    const waters = this.getAllWatersFromMixDesign(data.mixDesign);

    if (!waters.length) {
      return d;
    }

    // Iterate through each water and sum the results from getHeadWashWaterTarget
    waters.forEach(water => {
      const mft = data.dropSize * water.quantityNet;
      // These totals drive discharge and live moisture, so leave out skip water without a device: it is never fed.
      if (mft <= 0 || this.materialStore.isSkipMaterialWithoutDevice(water)) {
        return;
      }
      const waterTargets = this.getHeadWashWaterTarget(data, mft, water.code);
      d.headWater += waterTargets.headWater;
      d.washWater += waterTargets.washWater;
    });

    const sequence = this.getSequence(data);
    logger.log(sequence.name, data.currentDrop, data.allDrops, 'headWater: ', d.headWater, 'washWater', d.washWater);

    return d;
  },

  /**
   * Calculate drops
   *
   * @param data
   * @param maxDropSize
   * @returns {{allDrops: number, dropSize, batchSize: number}|*}
   */
  calculateDrops(this: IBatchStore, data: IBatchData, maxDropSize: number = this.maximumDropSize) {
    if (!data) {
      logger.warn('No data to calculate drops!');
      return;
    }

    const size = toNumber(data?.quantityToBeBatched || 0, 2);
    const truckSize = toNumber(data.mixQuantityInTruck || 0, 2);
    const batchSize = toNumber(Math.max(size - truckSize, 0), 2);
    const allDrops = toNumber(data.allDrops || 0) || (batchSize && maxDropSize ? Math.ceil(batchSize / maxDropSize) : 0);
    const dropSize = allDrops && toNumber(batchSize / allDrops, 2) > 0 ? toNumber(batchSize / allDrops, 2) : 0;

    return {
      allDrops,
      batchSize,
      dropSize,
    };
  },

  /**
   * Get initial material data to batch
   *
   * @param data
   * @returns {{allDrops: number, mixDesign: null, quantity: *, material: *, dropSize: number, batchSize: number,
   *   materialId: (null|*), mix: null, order: null}}
   */
  getInitialMaterialData(this: IBatchStore, data: IBatchData) {
    const material = this.materialStore.getMaterialByItemId(data.materialId);

    return {
      batchSize: 1,
      dropSize: 1,
      allDrops: 1,
      mix: null,
      order: null,
      mixDesign: null,
      materialId: data.materialId,
      quantity: data.quantity,
      material,
      mixerId: data.isWashout ? null : this.devicesStore.getNextMixer(),
      isWashout: data.isWashout,
      binPercent: data.binPercent || {},
    };
  },

  /**
   * Check if device is deferred on a given scale
   *
   * @param scaleId
   * @param deviceId
   * @returns {boolean}
   */
  isDeviceDeferred(this: IBatchStore, scaleId: number, deviceId: number) {
    return this.deferredFeedDevices.has(scaleId) && this.deferredFeedDevices.get(scaleId).has(deviceId);
  },

  isSmallLoadSize(this: IBatchStore, batchData: IBatchData): boolean {
    return !!this.smallLoadSize && batchData?.batchSize <= this.smallLoadSize;
  },

  getFreeFall(this: IBatchStore, bin: IBin, batchData: IBatchData) {
    if (FEATURE_AUTO_TUNING && bin?.autoTuning && bin?.autoFreeFall) {
      logger.log(`bin #${bin.id} auto free fall:`, bin.autoFreeFall);
      return bin.autoFreeFall || 0;
    }

    return (this.isSmallLoadSize(batchData) ? bin?.smallFreeFall : bin?.freeFall) || 0;
  },

  getInitialJogOpenTime(this: IBatchStore, bin: IBin, batchData: IBatchData) {
    if (FEATURE_AUTO_TUNING && bin?.autoTuning && bin?.autoJogOpenTime) {
      if (bin.autoJogOpenTime <= MAXIMUM_JOG_OPEN_TIME && bin.autoJogOpenTime >= MINIMUM_JOG_OPEN_TIME) {
        return bin.autoJogOpenTime;
      }
      logger.warn(`bin #${bin.id} auto jog open time out of range:`, bin.autoJogOpenTime);
    }

    return (this.isSmallLoadSize(batchData) ? bin?.smallJogOpenTime : bin?.jogOpenTime) || 0.5;
  },

  getJogCloseTime(this: IBatchStore, bin: IBin, batchData: IBatchData) {
    return (this.isSmallLoadSize(batchData) ? bin?.smallJogCloseTime : bin?.jogCloseTime) || 0.7;
  },

};
